import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, forkJoin, map, mergeMap, of, switchMap, takeUntil, tap } from 'rxjs';
import { getSeverityByStatusCode } from '../../../core/functions/app-insights-functions';
import { AppInsightsService } from '../../../core/services/app-insights/app-insights.service';
import { clearAllState, startTourIfUnseen } from '../../../core/store/actions/app.actions';
import { closeOnEscape } from '../../../shared/functions/dialog.functions';
import { TourType } from '../../../tours/data/tour-config';
import { patientSwitchInitiated } from '../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { NewMessageDialogComponent } from '../../components/new-message-dialog/new-message-dialog.component';
import { Message } from '../../models/message';
import { MessageResponsesService } from '../../services/message-responses/message-responses.service';
import { MessagesService } from '../../services/messages/messages.service';
import {
  selectMessageById,
  selectMessageCount,
  selectPageNumber,
  selectPageSize,
} from '../selectors/messages.selector';
import * as actions from './../actions/messages.actions';
import { closeNewMessageDialog, openNewMessageDialog } from './../actions/messages.actions';

@Injectable()
export class MessagesEffects {
  private actions$ = inject(Actions);
  private messagesService = inject(MessagesService);
  private messageResponsesService = inject(MessageResponsesService);
  private dialog = inject(MatDialog);
  private appInsightsService = inject(AppInsightsService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getMessages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getMessages, actions.setPageNumber, actions.retryPage),
      concatLatestFrom(() => [
        this.store.select(selectPatientId),
        this.store.select(selectPageNumber),
        this.store.select(selectPageSize),
      ]),
      mergeMap(([, patientId, pageNumber, pageSize]) => {
        return this.messagesService.postMessages(patientId, pageNumber, pageSize).pipe(
          takeUntil(this.cancelRequest$),
          map(response => {
            let isAtEndOfData = false;
            const totalRecordCount = response.totalRecordCount;
            let currentPage = pageNumber || 1;
            let messages = response.result;

            if (messages === null || messages.length === 0) {
              messages = [];
              currentPage--;
              isAtEndOfData = true;
            }

            if (messages.length < (pageSize || 10)) isAtEndOfData = true;
            return actions.getMessagesSuccess({
              messages,
              pageNumber: currentPage <= 0 ? 1 : currentPage,
              isAtEndOfData,
              totalRecordCount,
            });
          }),
          catchError(err => {
            return of(actions.getMessagesFailure({ error: err }));
          })
        );
      })
    );
  });

  sendMessage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.sendMessage),
      concatLatestFrom(() => this.store.select(selectPatientId)),
      mergeMap(([action, patientId]) => {
        return this.messagesService.postMessage(patientId, action.messageRequest).pipe(
          concatLatestFrom(() => this.store.select(selectPatientId)),
          // Patient switched mid-write: drop the response so patient A's message can't land in the reset slice.
          filter(([, currentPatientId]) => currentPatientId === patientId),
          map(([message]: [Message, number]) => {
            this.appInsightsService.trackEvent('Message.Send.Success');
            return actions.sendMessageSuccess({ message, continueAction: action.continueAction });
          }),
          catchError(err => {
            this.appInsightsService.trackTrace('Message.Send.Failure', getSeverityByStatusCode(err.status));
            return of(actions.sendMessageFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  sendMessageSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.sendMessageSuccess),
      map(action => {
        return action.continueAction;
      })
    );
  });

  getMessageResponses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getMessageResponses, actions.selectMessage),
      concatLatestFrom(action => [
        this.store.select(selectPatientId),
        this.store.select(selectMessageById(action.messageId)),
      ]),
      mergeMap(([action, patientId, message]) => {
        if (message == null) {
          return this.messagesService.getMessages(patientId).pipe(
            switchMap(messages =>
              forkJoin({
                messages: of(messages),
                messageResponses: this.messageResponsesService.getMessageResponses(patientId, action.messageId),
              })
            ),
            takeUntil(this.cancelRequest$),
            map(({ messages, messageResponses }) => {
              const currentMessage = messages.filter(m => m.messageId === action.messageId);
              const message = structuredClone(currentMessage[0]);
              message.messageResponses = messageResponses;

              return actions.getMessageResponsesSuccess({ message });
            }),
            catchError(err => {
              return of(actions.getMessageResponsesFailure({ error: err }));
            })
          );
        } else {
          return this.messageResponsesService.getMessageResponses(patientId, action.messageId).pipe(
            takeUntil(this.cancelRequest$),
            map(messageResponses => {
              const currentMessage = structuredClone(message);
              currentMessage.messageResponses = messageResponses;

              return actions.getMessageResponsesSuccess({ message: currentMessage });
            }),
            catchError(err => {
              return of(actions.getMessageResponsesFailure({ error: err }));
            })
          );
        }
      })
    );
  });

  sendMessageResponse$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.sendMessageResponse),
      concatLatestFrom(() => this.store.select(selectPatientId)),
      mergeMap(([action, patientId]) => {
        return this.messageResponsesService.postMessageResponse(patientId, action.messageResponseRequest).pipe(
          concatLatestFrom(() => this.store.select(selectPatientId)),
          // Patient switched mid-write: drop the response so patient A's reply can't land in the reset slice.
          filter(([, currentPatientId]) => currentPatientId === patientId),
          map(([messageResponse]) => {
            this.appInsightsService.trackEvent('Send Message Response Success');
            return actions.sendMessageResponseSuccess({ messageResponse });
          }),
          catchError(err => {
            this.appInsightsService.trackTrace('Send Message Response Failure', err.status);
            return of(actions.sendMessageResponseFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  openNewMessageDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(openNewMessageDialog),
        tap(() => {
          const dialogRef = this.dialog.open(NewMessageDialogComponent, {
            id: 'newMessageDialog',
            disableClose: true,
            autoFocus: false,
            width: '600px',
            minHeight: '600px',
            panelClass: 'new-message-dialog',
          });
          closeOnEscape(dialogRef);
        })
      );
    },
    { dispatch: false }
  );

  closeNewMessageDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(closeNewMessageDialog, actions.buildNewMessageDraft),
        tap(() => {
          this.dialog.getDialogById('newMessageDialog')?.close();
        })
      );
    },
    { dispatch: false }
  );

  startMessagesTourIfUnseen$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getMessagesSuccess),
      concatLatestFrom(() => this.store.select(selectMessageCount)),
      //only start messages tour if there are messages to show
      filter(([, count]) => count > 0),
      map(() => {
        return startTourIfUnseen({ tourType: TourType.Messages });
      })
    );
  });
}
